import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  FaUserPlus, FaKey, FaUserTie, FaTrash, FaArrowLeft, 
  FaUser, FaEnvelope, FaLock, FaUsers, FaUserEdit 
} from 'react-icons/fa';
import { ENDPOINT } from '../api/auth';
import axios from 'axios';
import './UserManagement.css';

const UserManagement = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('register');
  const [form, setForm] = useState({
    username: '',
    email: '',
    password: '',
    user_type: 'user',
    current_password: '',
    new_password: '',
    worker_name: ''
  });
  const [loading, setLoading] = useState(false);
  const [workers, setWorkers] = useState([]);
  const [users, setUsers] = useState([]);
  const [user, setUser] = useState(null);

  // Fetch current user data
  useEffect(() => {
    const fetchUser = async () => {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        setUser(JSON.parse(storedUser));
      }
    };
    fetchUser();
  }, []);

  // Fetch workers when in create_worker mode
  const fetchWorkers = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(ENDPOINT, {
        params: { action: 'get_workers' },
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setWorkers(response.data.workers || []);
      } else {
        throw new Error(response.data.message || 'Failed to fetch workers');
      }
    } catch (error) {
      console.error('Error fetching workers:', error);
      toast.error(error.response?.data?.message || 'Failed to load workers');
    }
  }, []);

  // Fetch users when in register mode
  const fetchUsers = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(ENDPOINT, {
        params: { action: 'get_users' },
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setUsers(response.data.users || []);
      } else {
        throw new Error(response.data.message || 'Failed to fetch users');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error(error.response?.data?.message || 'Failed to load users');
    }
  }, []);

  // Load workers when in create_worker mode, users when in register or manage_users mode
  useEffect(() => {
    if (mode === 'create_worker') {
      fetchWorkers();
    } else if (mode === 'register' || mode === 'manage_users') {
      fetchUsers();
    }
  }, [mode, fetchWorkers, fetchUsers]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      
      if (mode === 'create_worker') {
        if (!form.worker_name.trim()) throw new Error('Worker name is required');
        
        const response = await axios.post(
          ENDPOINT,
          {
            action: 'create_worker',
            name: form.worker_name,
            organization_id: user?.organization_id
          },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        
        if (response.data.success) {
          toast.success('Worker created successfully');
          setForm(prev => ({ ...prev, worker_name: '' }));
          fetchWorkers();
        } else {
          throw new Error(response.data.message || 'Failed to create worker');
        }
      } 
      else if (mode === 'register') {
        if (!form.username || !form.email || !form.password) {
          throw new Error('Please fill in all fields');
        }
        
        const response = await axios.post(
          ENDPOINT,
          {
            action: 'register_user',
            username: form.username,
            email: form.email,
            password: form.password,
            user_type: form.user_type
          },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        
        if (response.data.success) {
          toast.success('User registered successfully');
          setForm(prev => ({
            ...prev,
            username: '',
            email: '',
            password: '',
            user_type: 'user'
          }));
          fetchUsers();
        } else {
          throw new Error(response.data.message || 'Registration failed');
        }
      } 
      else if (mode === 'change_password') {
        if (!form.current_password || !form.new_password) {
          throw new Error('Please fill in all fields');
        }
        
        const response = await axios.post(
          ENDPOINT,
          {
            action: 'change_password',
            current_password: form.current_password,
            new_password: form.new_password
          },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        
        if (response.data.success) {
          toast.success('Password changed successfully');
          setForm(prev => ({
            ...prev,
            current_password: '',
            new_password: ''
          }));
        } else {
          throw new Error(response.data.message || 'Password change failed');
        }
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.message || error.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteWorker = (workerId) => {
    if (window.confirm('Are you sure you want to delete this worker? This action cannot be undone.')) {
      const deleteWorker = async () => {
        setLoading(true);
        try {
          const token = localStorage.getItem('token');
          const response = await axios.post(
            ENDPOINT,
            { action: 'delete_worker', worker_id: workerId },
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
          
          if (response.data.success) {
            toast.success('Worker deleted successfully');
            fetchWorkers();
          } else {
            throw new Error(response.data.message || 'Failed to delete worker');
          }
        } catch (error) {
          console.error('Error deleting worker:', error);
          toast.error(error.response?.data?.message || 'Failed to delete worker');
        } finally {
          setLoading(false);
        }
      };
      
      deleteWorker();
    }
  };

  const handleDeleteUser = (userId) => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      const deleteUser = async () => {
        setLoading(true);
        try {
          const token = localStorage.getItem('token');
          const response = await axios.post(
            ENDPOINT,
            { action: 'delete_user', user_id: userId },
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
          
          if (response.data.success) {
            toast.success('User deleted successfully');
            fetchUsers();
          } else {
            throw new Error(response.data.message || 'Failed to delete user');
          }
        } catch (error) {
          console.error('Error deleting user:', error);
          toast.error(error.response?.data?.message || 'Failed to delete user');
        } finally {
          setLoading(false);
        }
      };
      
      deleteUser();
    }
  };

  const renderForm = () => {
    if (mode === 'register') {
      return (
        <div className="form-section">
          <h2><FaUserPlus /> Register New User</h2>
          <div className="form-group">
            <label><FaUser /> Username</label>
            <input
              type="text"
              name="username"
              value={form.username}
              onChange={handleInputChange}
              className="form-control"
              placeholder="Enter username"
              required
            />
          </div>
          <div className="form-group">
            <label><FaEnvelope /> Email</label>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleInputChange}
              className="form-control"
              placeholder="Enter email"
              required
            />
          </div>
          <div className="form-group">
            <label><FaLock /> Password</label>
            <input
              type="password"
              name="password"
              value={form.password}
              onChange={handleInputChange}
              className="form-control"
              placeholder="Enter password"
              required
            />
          </div>
          <div className="form-group">
            <label><FaUserTie /> Role</label>
            <select
              name="user_type"
              value={form.user_type}
              onChange={handleInputChange}
              className="form-control"
              required
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
              <option value="worker">Worker</option>
            </select>
          </div>
          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Processing...' : 'Register User'}
          </button>
        </div>
      );
    } else if (mode === 'manage_users') {
      return (
        <div className="form-section">
          <h2><FaUsers /> Manage Users</h2>
          <div className="users-list">
            <h3>All Users in Organization</h3>
            {users.length === 0 ? (
              <p>No users found</p>
            ) : (
              <ul className="list-group">
                {users.map(userItem => (
                  <li key={userItem.id} className="list-group-item">
                    <div className="user-item">
                      <div className="user-info">
                        <span className="username">{userItem.username}</span>
                        <span className="email">({userItem.email})</span>
                        <span className={`role role-${userItem.role}`}>{userItem.role}</span>
                        <span className="status">{userItem.is_active ? 'Active' : 'Inactive'}</span>
                      </div>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDeleteUser(userItem.id)}
                        disabled={loading || userItem.id === user?.id}
                        title={userItem.id === user?.id ? "Cannot delete yourself" : "Delete user"}
                      >
                        <FaTrash /> Delete
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      );
    } else if (mode === 'change_password') {
      return (
        <div className="form-section">
          <h2><FaKey /> Change Password</h2>
          <div className="form-group">
            <label>Current Password</label>
            <input
              type="password"
              name="current_password"
              value={form.current_password}
              onChange={handleInputChange}
              className="form-control"
              placeholder="Enter current password"
              required
            />
          </div>
          <div className="form-group">
            <label>New Password</label>
            <input
              type="password"
              name="new_password"
              value={form.new_password}
              onChange={handleInputChange}
              className="form-control"
              placeholder="Enter new password"
              required
            />
          </div>
          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Updating...' : 'Update Password'}
          </button>
        </div>
      );
    } else if (mode === 'create_worker') {
      return (
        <div className="form-section">
          <h2><FaUserTie /> Manage Workers</h2>
          <div className="form-group">
            <label>Worker Name</label>
            <div className="input-group">
              <input
                type="text"
                name="worker_name"
                value={form.worker_name}
                onChange={handleInputChange}
                className="form-control"
                placeholder="Enter worker name"
                required
              />
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={loading || !form.worker_name.trim()}
              >
                {loading ? 'Adding...' : 'Add Worker'}
              </button>
            </div>
          </div>
          
          <div className="workers-list">
            <h3>Existing Workers</h3>
            {workers.length === 0 ? (
              <p>No workers found</p>
            ) : (
              <ul className="list-group">
                {workers.map(worker => (
                  <li key={worker.id} className="list-group-item">
                    <div className="worker-item">
                      <span>{worker.name}</span>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDeleteWorker(worker.id)}
                        disabled={loading}
                      >
                        <FaTrash /> Delete
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      );
    }
  };

  return (
    <div className="user-management">
      <div className="page-header">
        <h1><FaUserEdit /> User Management</h1>
        <p>Manage users, workers, and account settings</p>
      </div>
      
      <div className="mode-selector">
        <button
          className={`mode-btn ${mode === 'register' ? 'active' : ''}`}
          onClick={() => setMode('register')}
        >
          <FaUserPlus /> Register User
        </button>
        <button
          className={`mode-btn ${mode === 'change_password' ? 'active' : ''}`}
          onClick={() => setMode('change_password')}
        >
          <FaKey /> Change Password
        </button>
        <button
          className={`mode-btn ${mode === 'create_worker' ? 'active' : ''}`}
          onClick={() => setMode('create_worker')}
        >
          <FaUserTie /> Manage Workers
        </button>
        <button
          className={`mode-btn ${mode === 'manage_users' ? 'active' : ''}`}
          onClick={() => setMode('manage_users')}
        >
          <FaUsers /> Manage Users
        </button>
      </div>
      
      <div className="card">
        <form onSubmit={handleSubmit}>
          {renderForm()}
        </form>
      </div>
    </div>
  );
};

export default UserManagement;
